/**
 * Lightweight console + global-error capture for crash reports.
 *
 * Installed once at app start (main.tsx). Wraps console.log/info/warn/error and
 * window error / unhandledrejection into a bounded ring buffer, so when the app
 * hits the 500 error boundary we can attach the recent console log to the
 * GitHub crash report. Defensive: logging must NEVER throw, and the original
 * console output is always preserved.
 */
export interface LogEntry {
  t: number
  level: string
  msg: string
}

const BUFFER: LogEntry[] = []
const MAX_ENTRIES = 120
const MAX_MSG_CHARS = 2000
let installed = false

function stringifyArg(a: unknown): string {
  if (a instanceof Error) return `${a.message}\n${a.stack ?? ''}`
  if (typeof a === 'string') return a
  if (typeof a === 'object' && a !== null) {
    try {
      return JSON.stringify(a)
    } catch {
      return String(a)
    }
  }
  return String(a)
}

function push(level: string, args: unknown[]): void {
  try {
    const msg = args.map(stringifyArg).join(' ').slice(0, MAX_MSG_CHARS)
    BUFFER.push({ t: Date.now(), level, msg })
    if (BUFFER.length > MAX_ENTRIES) BUFFER.shift()
  } catch {
    /* never throw from logging */
  }
}

export function installConsoleCapture(): void {
  if (installed || typeof window === 'undefined') return
  installed = true

  const levels = ['log', 'info', 'warn', 'error'] as const
  for (const level of levels) {
    const orig = console[level].bind(console)
    console[level] = (...args: unknown[]) => {
      push(level, args)
      orig(...args)
    }
  }

  window.addEventListener('error', (e) => {
    push('error', [e.message, (e.error as Error | undefined)?.stack ?? ''])
  })
  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason as { stack?: string } | undefined
    push('error', ['unhandledrejection:', r?.stack ?? String(e.reason)])
  })
}

/** The captured log as text (most recent last), optionally limited by chars. */
export function getConsoleLog(maxChars = 100_000): string {
  const text = BUFFER.map((e) => {
    const ts = new Date(e.t).toISOString().slice(11, 23)
    return `[${ts}] ${e.level.toUpperCase()}: ${e.msg}`
  }).join('\n')
  return text.length > maxChars ? text.slice(-maxChars) : text
}
